import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { choreographyApi } from '../../services/api';
import { projectChoreoCanvas } from './blueprintComposer';
import { CommunityReviewLayer, COMMUNITY_REVIEW_DISPLAYS } from './CommunityReview';
import { ContinuousCanvasEditor, scoreFromSnapshot, sourceFromSnapshot } from './MusicChoreography';
import './MusicChoreography.css';

const responseData = (response) => response?.data?.data || response?.data || {};
const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const linkFragmentHandoff = new Map();
const linkProjectionRequests = new Map();

function routineFromLinkProjection(response, routineId) {
  const projection = responseData(response);
  if (!isObject(projection) || projection.resourceType !== 'choreo_sheet'
    || projection.resourceId !== routineId || !isObject(projection.fields)
    || projection.fields.routineId !== routineId || typeof projection.fields.title !== 'string'
    || !isObject(projection.fields.snapshot)) throw new Error('This share link is not available.');
  return { ...projection.fields, shareScope: 'link' };
}

export default function SharedChoreography() {
  const { routineId } = useParams();
  const [routine, setRoutine] = useState(null);
  const [error, setError] = useState('');
  const [reviewDisplay, setReviewDisplay] = useState('inline');
  const sourceRef = useRef(null);
  const linkTokenRef = useRef(undefined);
  const requestRef = useRef({ routineId: null, promise: null });
  const loadPublicCommunity = useCallback((id) => choreographyApi.getPublicCommunity(id), []);

  if (linkTokenRef.current === undefined) {
    const rawFragment = window.location.hash.slice(1);
    try { linkTokenRef.current = rawFragment ? decodeURIComponent(rawFragment) : ''; } catch { linkTokenRef.current = ''; }
  }

  useEffect(() => {
    let active = true;
    if (requestRef.current.routineId !== routineId) {
      if (linkTokenRef.current) {
        linkFragmentHandoff.set(routineId, linkTokenRef.current);
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
        linkTokenRef.current = '';
      }
      const token = linkFragmentHandoff.get(routineId) || '';
      let promise;
      if (token) {
        promise = linkProjectionRequests.get(routineId);
        if (!promise) {
          promise = choreographyApi.getSharingLinkProjection(token)
            .then((response) => routineFromLinkProjection(response, routineId));
          linkProjectionRequests.set(routineId, promise);
          const clearHandoff = () => {
            if (linkProjectionRequests.get(routineId) === promise) linkProjectionRequests.delete(routineId);
            linkFragmentHandoff.delete(routineId);
          };
          promise.then(clearHandoff, clearHandoff);
        }
      } else {
        promise = choreographyApi.getSharedRoutine(routineId).then(responseData);
      }
      requestRef.current = {
        routineId,
        promise,
      };
    }
    const request = requestRef.current.promise;
    request
      .then((value) => { if (active) setRoutine(value); })
      .catch((cause) => { if (active) setError(cause?.response?.data?.message || cause?.response?.data?.error?.message || cause?.message || 'This routine is not available.'); });
    return () => { active = false; };
  }, [routineId]);

  if (error) return <main className="mc-shared-page"><section className="mc-panel mc-shared-card"><span className="mc-kicker">Shared choreography</span><h1>Routine unavailable</h1><p>{error}</p><Link to="/login">Sign in</Link></section></main>;
  if (!routine) return <main className="mc-shared-page"><section className="mc-panel mc-shared-card"><p>Loading shared routine…</p></section></main>;

  const snapshot = routine.snapshot || {};
  const source = sourceFromSnapshot(snapshot);
  const projectedScore = projectChoreoCanvas(source);
  const score = projectedScore.sections.some(({ allocations }) => allocations.length > 0)
    ? projectedScore
    : scoreFromSnapshot(snapshot);
  return <main className="mc-shared-page"><section className="mc-panel mc-shared-card">
    <span className="mc-kicker">{routine.organisationName || 'StudioAeye'} · shared choreography</span>
    <h1>{routine.title}</h1>
    <p className="mc-muted">{routine.track?.title}{routine.track?.artist ? ` · ${routine.track.artist}` : ''} · Revision {routine.revisionNumber}</p>
    {routine.shareScope === 'everyone' && <label className="mc-field mc-review-display-field"><span>Review</span><select aria-label="Review display" value={reviewDisplay} onChange={(event) => setReviewDisplay(event.target.value)}>{COMMUNITY_REVIEW_DISPLAYS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></label>}
    <div className={`mc-community-canvas-layout mc-shared-community-layout mc-review-${reviewDisplay}-layout`}>
      <div className="mc-blank-page mc-blank-page-canvas">
        {source ? <ContinuousCanvasEditor inputRef={sourceRef} value={source} score={score} onChange={() => {}} ariaLabel="Published choreography source" readOnly /> : <p className="mc-empty">This published routine does not have song sections yet.</p>}
        {(snapshot.cues || []).length > 0 && <div className="mc-shared-cues"><h2>Cues</h2>{snapshot.cues.map((cue) => <p key={cue.id}><strong>Count {cue.atCount}</strong> · {cue.text || cue.label || cue.title}</p>)}</div>}
      </div>
      {routine.shareScope === 'everyone' && <CommunityReviewLayer routineId={routineId} revisionId={routine.publishedRevisionId} source={source} sourceRef={sourceRef} anonymous readOnly display={reviewDisplay} loadCommunity={loadPublicCommunity} />}
    </div>
  </section></main>;
}
